import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Target,
  Tag,
  Calendar,
  User,
  Building2,
  TrendingUp,
  Loader2,
  Plus,
  Users,
  ShoppingCart,
  Trash2,
  Package,
  AlertCircle,
  Percent,
  Edit,
  List,
  Check,
  X,
  Coins,
  Globe,
  Flag,
  DollarSign,
  UserCircle,
  Send,
  ArrowRightLeft,
  CalendarDays,
  FileText,
  UserCheck,
} from "lucide-react";
import { FileUploader, Attachment } from "@/components/shared/FileUploader";
import { useToast } from "@/hooks/use-toast";
import { usePipelinePermissions } from "@/hooks/usePipelinePermissions";
import {
  opportunityAPI,
  Opportunity,
  OpportunityItem,
  AddItemWithoutRulesData,
  AddItemWithRulesData,
} from "@/services/opportunityService";
import { opportunitySourcesAPI } from "@/services/opportunitySourcesService";
import { companyAPI } from "@/services/companyService";
import { contactoAPI, Contacto } from "@/services/contactoService";
import { OpportunityStage } from "@/services/opportunityService";
import { currencyAPI, Currency } from "@/services/currencyService";
import { CurrencyModal } from "./CurrencyModal";
import { usersService } from "@/services/usersService";
import type { User as SystemUser } from "@/types/crm";
import {
  productService,
  Product as ServiceProduct,
} from "@/services/product.service";
import { AddProductModal } from "./AddProductModal";
import { CreateRuleModal } from "@/components/products/CreateRuleModal";
import { Badge } from "@/components/ui/badge";
import { pipelineAPI } from "@/services/pipelineService";
import { useAuth } from "@/hooks/useAuth";
import { CreateCompanyModal } from "@/components/companies/CreateCompanyModal";
import { CreateContactModal } from "@/components/contacts/CreateContactModal";
import { OpportunitySourcesModal } from "./OpportunitySourcesModal";
import { separateRules } from "@/utils/ruleUtils";

interface EditOpportunityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (newId?: string) => void;
  slug: string;
  opportunity: Opportunity | null;
}

interface Product extends ServiceProduct {
  price?: number;
}

const formatCurrency = (value: number) => {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDisplayValue = (value: string | number) => {
  if (value === undefined || value === null) return "";
  const strValue = value.toString();
  if (!strValue) return "";
  const [integer, decimal] = strValue.split(".");
  const formattedInteger = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimal !== undefined
    ? `${formattedInteger}.${decimal}`
    : formattedInteger;
};

export function EditOpportunityModal({
  isOpen,
  onClose,
  onSuccess,
  slug,
  opportunity,
}: EditOpportunityModalProps) {
  const { toast } = useToast();
  const { user: authUser, company } = useAuth();
  const { canEditPipeline } = usePipelinePermissions();

  const [formData, setFormData] = useState({
    name: "",
    estimated_value: "0",
    success_probability: "50",
    stage_id: "",
    estimated_close_date: "",
    contact_id: "",
    client_company_id: "",
    opportunity_source_id: "",
    notes: "",
    priority: "Mediana",
    currency_id: "",
    created_by: "",
    responsible_id: "", // Added responsible_id to formData
    lost_reason: "",
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [showCurrencyModal, setShowCurrencyModal] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  const [isEditingExisting, setIsEditingExisting] = useState(false);
  const [discount, setDiscount] = useState({
    value: "0",
    type: "percentage" as "fixed" | "percentage",
  });
  const [selectedCollaboratorIds, setSelectedCollaboratorIds] = useState<
    string[]
  >([]);
  // Store the original creator ID to enforce responsible stays as creator
  const [creatorId, setCreatorId] = useState<string>("");
  // Store the original responsible ID (could be creator or assigned)
  const [originalResponsibleId, setOriginalResponsibleId] =
    useState<string>("");

  const [selectedItemForRules, setSelectedItemForRules] = useState<any>(null);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showSourceModal, setShowSourceModal] = useState(false);

  const [opportunityItems, setOpportunityItems] = useState<OpportunityItem[]>(
    [],
  );
  const [opportunityTotals, setOpportunityTotals] = useState({
    subtotal: 0,
    tax: 0,
    total: 0,
  });

  const [currentOpportunityId, setCurrentOpportunityId] = useState<
    string | null
  >(opportunity?.id || null);
  const [detailedOpportunity, setDetailedOpportunity] =
    useState<Opportunity | null>(null);
  const [contacts, setContacts] = useState<Contacto[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [stages, setStages] = useState<OpportunityStage[]>([]);
  const isLostStage = useMemo(() => {
    const stage = (stages as OpportunityStage[]).find(
      (s) => s.id === formData.stage_id,
    );
    return stage?.stage_type === "closed_lost";
  }, [formData.stage_id, stages]);

  const [sources, setSources] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [users, setUsers] = useState<SystemUser[]>([]);
  const [products, setProducts] = useState<Product[]>([]);

  const [loading, setLoading] = useState({
    contacts: false,
    companies: false,
    stages: false,
    sources: false,
    currencies: false,
    users: false,
    products: false,
    items: false,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const currencySymbol = useMemo(() => {
    if ((company as any)?.currency) return (company as any).currency;
    try {
      const storedCompany = localStorage.getItem("company");
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        if (parsed.currency) return parsed.currency;
      }
    } catch (e) {}
    return "Q";
  }, [company]);

  useEffect(() => {
    if (isOpen && slug) {
      fetchData();
    }
  }, [isOpen, slug, opportunity]);

  const fetchData = async () => {
    console.log("🔍 fetchData start");
    setIsFetching(true);
    try {
      // Fetch latest opportunity data
      const latestResp = await opportunityAPI
        .getOpportunityById(opportunity?.id, slug)
        .catch(() => null);
      const latestOpp = latestResp?.data || opportunity;
      console.log("🔍 fetchData: latestOpp", latestOpp);

      // Prepare stages params
      const stagesParams: any = { slug, is_active: true };
      if (opportunity?.pipeline_id) {
        stagesParams.pipeline_id = opportunity.pipeline_id;
      }

      // Parallel fetch supporting data
      setLoading((prev) => ({ ...prev, users: true }));
      const [
        contactsData,
        companiesResponse,
        stagesResponse,
        sourcesResponse,
        currenciesResponse,
        usersResponse,
        productsResponse,
      ] = await Promise.all([
        contactoAPI.getContactos(slug),
        companyAPI.getCompanies({ slug }),
        opportunityAPI.getStages(stagesParams),
        opportunitySourcesAPI.getSources({ slug, is_active: true }),
        currencyAPI.getCurrencies({ slug, is_active: true }),
        usersService.getUsers(slug, 1, 1000, true),
        productService
          .getProducts({ slug, limit: 100, is_active: true })
          .catch(() => ({ data: [] })),
      ]);
      setLoading((prev) => ({ ...prev, users: false }));

      // Set state with fetched data
      setContacts(contactsData);
      setCompanies(companiesResponse.data);
      setStages(stagesResponse.data);
      setSources(sourcesResponse.data);
      setCurrencies(currenciesResponse.data);
      setProducts((productsResponse.data || []).filter((p) => p.is_active));
      setUsers(usersResponse.data || []);

      // Determine default currency
      const defaultCurrency = currenciesResponse.data.find(
        (c: Currency) => c.is_default,
      );
      const defaultCurrencyId =
        defaultCurrency?.id || currenciesResponse.data[0]?.id || "";

      // Set detailed opportunity and form data
      setDetailedOpportunity(latestOpp);
      setCurrentOpportunityId(latestOpp.id);

      // Save creator ID for later enforcement
      const creator =
        (latestOpp as any).creator?.id || latestOpp.created_by || "";
      setCreatorId(creator);
      // Save original responsible ID (if any)
      const originalResp = latestOpp.responsible_id || creator;
      setOriginalResponsibleId(originalResp);

      const safeId = (obj: any, idProp?: any, altIdProp?: any) => {
        if (
          idProp &&
          typeof idProp === "string" &&
          idProp.length > 5 &&
          idProp !== "null" &&
          idProp !== "undefined"
        )
          return idProp;
        if (
          altIdProp &&
          typeof altIdProp === "string" &&
          altIdProp.length > 5 &&
          altIdProp !== "null" &&
          altIdProp !== "undefined"
        )
          return altIdProp;
        if (obj && typeof obj === "object") {
          const val = obj.id || obj.ID || obj._id;
          if (val && typeof val === "string" && val.length > 5) return val;
        }
        if (
          typeof obj === "string" &&
          obj.length > 5 &&
          obj !== "null" &&
          obj !== "undefined"
        )
          return obj;
        return "";
      };

      const formatDateForInput = (dateString: string | null) => {
        if (!dateString) return "";
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return "";
          return date.toISOString().split("T")[0];
        } catch {
          return "";
        }
      };

      // Clean notes and extract discount
      let cleanNotes = latestOpp.notes || "";
      const discountMatch = latestOpp.notes?.match(/\[DISCOUNT:({.*})\]/);
      if (discountMatch) {
        try {
          const parsed = JSON.parse(discountMatch[1]);
          setDiscount(parsed);
          cleanNotes = cleanNotes.replace(discountMatch[0], "").trim();
        } catch (e) {
          console.error("Error parsing discount", e);
        }
      }

      // Populate form data
      setFormData({
        name: latestOpp.name || "",
        estimated_value: latestOpp.estimated_value?.toString() || "0",
        success_probability: latestOpp.success_probability?.toString() || "50",
        stage_id: safeId(
          latestOpp.stage_id,
          latestOpp.stage,
          (latestOpp as any).stageId,
        ),
        estimated_close_date: formatDateForInput(
          latestOpp.estimated_close_date,
        ),
        contact_id: safeId(
          (latestOpp as any).contact_id,
          latestOpp.contact,
          (latestOpp as any).contactId,
        ),
        client_company_id: safeId(
          (latestOpp as any).client_company_id,
          latestOpp.client_company,
          (latestOpp as any).clientCompanyId,
        ),
        opportunity_source_id: safeId(
          (latestOpp as any).opportunity_source_id,
          latestOpp.opportunity_source,
        ),
        notes: cleanNotes,
        priority: (latestOpp as any).priority || "Mediana",
        currency_id:
          safeId(
            (latestOpp as any).currency_id,
            latestOpp.currency,
            (latestOpp as any).currencyId,
          ) || defaultCurrencyId,
        created_by: (latestOpp as any).created_by || "",
        responsible_id: safeId(
          latestOpp.responsible_id,
          latestOpp.created_by,
          (latestOpp as any).creator,
        ),
        lost_reason: latestOpp.lost_reason || "",
      });
      console.log(
        "🔍 Responsible ID in formData after fetch:",
        latestOpp.responsible_id || (latestOpp as any).created_by,
      );
      // Store for fallback if needed
      const respId =
        latestOpp.responsible_id || (latestOpp as any).created_by || "";
      setOriginalResponsibleId(respId);
      setSelectedCollaboratorIds(
        latestOpp.collaborators?.map((c) => c.user_id) || [],
      );
      const initialItems = latestOpp.items || [];
      setOpportunityItems(initialItems);
      setOpportunityTotals({
        subtotal: parseFloat(latestOpp.subtotal?.toString() || "0"),
        tax: parseFloat(latestOpp.tax?.toString() || "0"),
        total: parseFloat(latestOpp.total?.toString() || "0"),
      });
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setIsFetching(false);
    }
  };
  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    console.log(`🔧 handleChange: ${field} changed to`, value);
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleCurrencyInputChange = (
    field: keyof typeof formData,
    value: string,
  ) => {
    const rawValue = value.replace(/,/g, "");
    const sanitizedValue = rawValue.replace(/[^0-9.]/g, "");
    handleChange(field, sanitizedValue);
  };

  const handleDiscountChange = (value: string) => {
    const rawValue = value.replace(/,/g, "");
    const sanitizedValue = rawValue.replace(/[^0-9.]/g, "");
    setDiscount((prev) => ({ ...prev, value: sanitizedValue }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    console.log("🔍 Iniciando validación de formulario...");
    console.log("Valores actuales en formData:", formData);

    if (!formData.name) {
      console.log("❌ Error en validación: 'name' está vacío:", formData.name);
      newErrors.name = "El nombre es requerido";
    }
    if (!formData.stage_id) {
      console.log(
        "❌ Error en validación: 'stage_id' está vacío:",
        formData.stage_id,
      );
      newErrors.stage_id = "La etapa es requerida";
    }
    if (!formData.contact_id) {
      console.log(
        "❌ Error en validación: 'contact_id' está vacío:",
        formData.contact_id,
      );
      newErrors.contact_id = "El contacto es requerido";
    }
    if (!formData.client_company_id) {
      console.log(
        "❌ Error en validación: 'client_company_id' está vacío:",
        formData.client_company_id,
      );
      newErrors.client_company_id = "La empresa es requerida";
    }

    if (isLostStage && !formData.lost_reason) {
      console.log(
        "❌ Error en validación: 'lost_reason' está vacío en etapa Perdida",
      );
      newErrors.lost_reason = "El motivo de pérdida es requerido";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      console.log("⚠️ Validación fallida. Errores encontrados:", newErrors);
      toast({
        title: "Error de validación",
        description: "Por favor, completa todos los campos obligatorios",
        variant: "destructive",
      });
      return false;
    }

    console.log("✅ Validación exitosa. Procediendo a guardar...");
    return true;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (isSaving) return;
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const finalNotes =
        discount.value !== "0"
          ? `${formData.notes}\n\n[DISCOUNT:${JSON.stringify(discount)}]`
          : formData.notes;

      let oppId = currentOpportunityId || opportunity?.id || "";

      let finalEstimatedValue = parseFloat(formData.estimated_value) || 0;

      if (opportunityItems.length > 0) {
        const currentSubtotal =
          opportunityItems.reduce(
            (acc, item) => acc + parseFloat(item.line_total?.toString() || "0"),
            0,
          ) + opportunityTotals.tax;
        let finalDiscountAmount = 0;
        if (discount.value !== "0") {
          const discountVal = parseFloat(discount.value) || 0;
          if (discount.type === "percentage") {
            finalDiscountAmount = (currentSubtotal * discountVal) / 100;
          } else {
            finalDiscountAmount = discountVal;
          }
        }
        finalEstimatedValue = Math.max(
          0,
          currentSubtotal - finalDiscountAmount,
        );
      }

      const dataToSave: any = {
        ...formData,
        opportunity_source_id: formData.opportunity_source_id || null,
        contact_id: formData.contact_id || null,
        client_company_id: formData.client_company_id || null,
        currency_id: formData.currency_id || null,
        success_probability: parseFloat(formData.success_probability) || 0,
        notes: finalNotes,
        slug,
        user: authUser,
        collaborators: selectedCollaboratorIds.map((id) => ({ user_id: id })),
        estimated_value: finalEstimatedValue,
        // Use form responsible if set, otherwise fallback to original responsible or creator
        // Set responsible to the creator of the opportunity (owner)
        responsible_id: creatorId || null,
        items: opportunityItems.map((item: any) => ({
          id: item.id?.startsWith("_new_") ? undefined : item.id,
          product_id: item.product_id || null,
          description: item.description || "",
          notes: item.notes || "",
          quantity: item.quantity,
          base_price: parseFloat(item.base_price?.toString() || "0"),
          applied_rules: item.applied_rules?.map((r: any) => ({
            rule_id: r.rule_id,
            rule_name: r.rule_name,
            is_custom: r.is_custom,
            price_modifier: r.price_modifier,
            price_type: r.price_type,
            description: r.description,
            value: r.value || "1",
          })),
        })),
      };

      if (oppId) {
        const response = await opportunityAPI.updateOpportunity(
          oppId,
          dataToSave,
        );

        // Determinar el ID final (podría ser uno nuevo si se clonó la oportunidad)
        const finalId = response.data?.id || oppId;

        // 3. Upload attachments if any in parallel
        if (attachments && attachments.length > 0) {
          console.log(
            `Subiendo ${attachments.length} archivos para la oportunidad ${finalId} en paralelo`,
          );
          await Promise.all(
            attachments.map(async (att) => {
              try {
              await opportunityAPI.uploadAttachment(
                finalId,
                slug,
                att.file,
                att.description || "Archivo adjunto",
              );
              } catch (err) {
                console.error("Error al subir archivo adjunto:", err);
                toast({
                  title: "Error parcial",
                  description: `No se pudo subir el archivo ${att.file.name}`,
                  variant: "destructive",
                });
              }
            }),
          );
        }

        if (finalId !== oppId) {
          onSuccess(finalId);
        } else {
          onSuccess();
        }
      }

      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "No se pudo guardar la oportunidad",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // --- Handlers that only mutate local state (staging) ---
  const handleAddOrUpdateProduct = (productData: any) => {
    const isEdit = isEditingExisting && productData.id;

    const basePrice = parseFloat(productData.base_price) || 0;
    const qty = productData.quantity || 1;
    const baseSubtotal = basePrice * qty;
    const rulesTotal = (productData.applied_rules || []).reduce(
      (acc: number, r: any) => {
        const modifier = parseFloat(r.price_modifier) || 0;
        if (r.price_type === "percentage") {
          return acc + baseSubtotal * (modifier / 100);
        } else if (r.price_type === "per_unit") {
          return acc + modifier * qty;
        } else {
          return acc + modifier;
        }
      },
      0,
    );
    const lineTotal = baseSubtotal + rulesTotal;

    setOpportunityItems((prevItems) => {
      let updated: OpportunityItem[];
      if (isEdit) {
        const existingItem = prevItems.find((i) => i.id === productData.id);
        const catalogPrice = parseFloat(
          (existingItem as any)?.original_price?.toString() ||
            (existingItem?.product as any)?.base_price?.toString() ||
            basePrice.toString(),
        );
        const hasDeviation = Math.abs(basePrice - catalogPrice) > 0.01;
        updated = prevItems.map((item) =>
          item.id === productData.id
            ? {
                ...item,
                ...productData,
                has_deviation: hasDeviation,
                line_total: lineTotal.toFixed(2),
              }
            : item,
        );
      } else {
        const catalogProduct = products.find(
          (p) => p.id === productData.product_id,
        );
        const catalogPrice = catalogProduct
          ? parseFloat(catalogProduct.base_price?.toString()) || 0
          : basePrice;
        const hasDeviation = Math.abs(basePrice - catalogPrice) > 0.01;
        const tempId = `_new_${Date.now()}`;
        const newItem: any = {
          id: tempId,
          _isNew: true,
          product_id: productData.product_id || null,
          description: productData.description || productData.manualName || "",
          notes: (productData as any).notes || "",
          product: catalogProduct || null,
          quantity: qty,
          base_price: basePrice.toFixed(2),
          original_price: catalogPrice.toFixed(2),
          has_deviation: hasDeviation,
          applied_rules: productData.applied_rules || [],
          line_total: lineTotal.toFixed(2),
        };
        updated = [...prevItems, newItem];
      }

      const newSubtotal = updated.reduce(
        (s, i) => s + parseFloat(i.line_total?.toString() || "0"),
        0,
      );
      setOpportunityTotals((prev) => ({
        ...prev,
        subtotal: newSubtotal,
        total: newSubtotal + prev.tax,
      }));
      setFormData((prev) => ({
        ...prev,
        estimated_value: newSubtotal.toFixed(2),
      }));
      return updated;
    });

    setShowAddProductModal(false);
    setIsEditingExisting(false);
  };

  const handleDeleteItem = (itemId: string) => {
    setOpportunityItems((prevItems) => {
      const updated = prevItems.filter((i) => i.id !== itemId);
      const newSubtotal = updated.reduce(
        (s, i) => s + parseFloat(i.line_total?.toString() || "0"),
        0,
      );
      setOpportunityTotals((prev) => ({
        ...prev,
        subtotal: newSubtotal,
        total: newSubtotal + prev.tax,
      }));
      setFormData((prev) => ({
        ...prev,
        estimated_value: newSubtotal.toFixed(2),
      }));
      return updated;
    });
  };

  const getUserDisplayName = (user: SystemUser): string => {
    if (user.first_name || user.last_name) {
      return `${user.first_name || ""} ${user.last_name || ""}`.trim();
    }
    return user.nombre || user.name || "";
  };

  const calculateTotalWithDiscount = () => {
    const currentTotal = opportunityTotals.subtotal + opportunityTotals.tax;
    let discountAmount = 0;
    const discountVal = parseFloat(discount.value) || 0;

    if (discount.type === "percentage") {
      discountAmount = (currentTotal * discountVal) / 100;
    } else {
      discountAmount = discountVal;
    }

    return Math.max(0, currentTotal - discountAmount);
  };

  const getDetailedTotals = () => {
    let baseSubtotal = 0;
    let extrasSubtotal = 0;

    opportunityItems.forEach((item: any) => {
      const qty = item.quantity || 1;
      const numericBasePrice = parseFloat(item.base_price?.toString() || "0");
      const itemBaseTotal = numericBasePrice * qty;
      baseSubtotal += itemBaseTotal;

      const lineTotal = parseFloat(item.line_total?.toString() || "0");
      extrasSubtotal += lineTotal - itemBaseTotal;
    });

    const finalTotal = calculateTotalWithDiscount();
    const currentTotal = opportunityTotals.subtotal + opportunityTotals.tax;
    const discountAmount = Math.max(0, currentTotal - finalTotal);

    return {
      baseSubtotal,
      extrasSubtotal,
      subtotal: baseSubtotal + extrasSubtotal,
      tax: opportunityTotals.tax,
      discountAmount,
      finalTotal,
    };
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0">
          {isFetching && (
            <div className="absolute inset-0 z-50 bg-white/70 backdrop-blur-[1px] flex flex-col items-center justify-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <p className="text-sm font-semibold text-slate-600">
                Cargando datos...
              </p>
            </div>
          )}
          {isSaving && (
            <div className="absolute inset-0 z-50 bg-white/70 backdrop-blur-[1px] flex flex-col items-center justify-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              <p className="text-sm font-semibold text-slate-600">
                Guardando cambios...
              </p>
            </div>
          )}
          <form
            onSubmit={handleSubmit}
            className="flex flex-col h-full max-h-[90vh]"
          >
            <DialogHeader className="p-6 pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <DialogTitle className="text-xl font-bold flex items-center gap-2">
                    <Edit className="h-5 w-5 text-blue-600" />
                    Editar Oportunidad:{" "}
                    {detailedOpportunity?.name || opportunity?.name}
                  </DialogTitle>
                  <DialogDescription>
                    Versión V
                    {detailedOpportunity?.version_number ||
                      opportunity?.version_number}
                    .
                    {detailedOpportunity?.minor_version ??
                      opportunity?.minor_version ??
                      0}{" "}
                    · ID: #
                    {(detailedOpportunity?.id || opportunity?.id)
                      ?.substring(0, 8)
                      .toUpperCase()}
                  </DialogDescription>
                </div>
                {!opportunity?.is_latest_version && (
                  <Badge
                    variant="destructive"
                    className="uppercase text-[10px]"
                  >
                    Historial / Lectura
                  </Badge>
                )}
              </div>
            </DialogHeader>

            <div className="flex-1 overflow-y-auto p-6 pt-2 custom-scrollbar">
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="name" className="flex items-center">
                      <Target className="h-4 w-4 mr-2" />
                      Nombre de la Oportunidad *
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleChange("name", e.target.value)}
                      placeholder="Nombre de la oportunidad"
                      className="mt-1"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-xs mt-1 font-medium">
                        {errors.name}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label
                      htmlFor="estimated_value"
                      className="flex items-center"
                    >
                      <Coins className="h-4 w-4 mr-2" />
                      Valor Estimado
                    </Label>
                    <div className="relative mt-1">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <span className="text-gray-500 font-medium">
                          {currencySymbol}
                        </span>
                      </div>
                      <Input
                        id="estimated_value"
                        value={formatDisplayValue(formData.estimated_value)}
                        onChange={(e) =>
                          handleCurrencyInputChange(
                            "estimated_value",
                            e.target.value,
                          )
                        }
                        className="pl-10"
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  <div>
                    <Label
                      htmlFor="success_probability"
                      className="flex items-center"
                    >
                      <TrendingUp className="h-4 w-4 mr-2" />
                      Probabilidad (%)
                    </Label>
                    <Select
                      onValueChange={(val) =>
                        handleChange("success_probability", val)
                      }
                      value={formData.success_probability}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10% - Muy baja</SelectItem>
                        <SelectItem value="25">25% - Baja</SelectItem>
                        <SelectItem value="50">50% - Media</SelectItem>
                        <SelectItem value="75">75% - Alta</SelectItem>
                        <SelectItem value="90">90% - Muy alta</SelectItem>
                        <SelectItem value="100">
                          100% - Cerrada ganada
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="stage_id" className="flex items-center">
                      <List className="h-4 w-4 mr-2" />
                      Etapa *
                    </Label>
                    <Select
                      key={stages.length}
                      value={formData.stage_id?.toString() || ""}
                      onValueChange={(val) => handleChange("stage_id", val)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Seleccionar etapa" />
                      </SelectTrigger>
                      <SelectContent>
                        {stages.map((stage) => (
                          <SelectItem key={stage.id} value={stage.id}>
                            <div className="flex items-center gap-2">
                              {stage.color && (
                                <div
                                  className="w-2 h-2 rounded-full"
                                  style={{ backgroundColor: stage.color }}
                                />
                              )}
                              {stage.name}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label
                      htmlFor="estimated_close_date"
                      className="flex items-center"
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Cierre Estimado
                    </Label>
                    <Input
                      type="date"
                      id="estimated_close_date"
                      value={formData.estimated_close_date}
                      onChange={(e) =>
                        handleChange("estimated_close_date", e.target.value)
                      }
                      className="mt-1"
                    />
                    {errors.estimated_close_date && (
                      <p className="text-red-500 text-xs mt-1 font-medium">
                        {errors.estimated_close_date}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label
                      htmlFor="client_company_id"
                      className="flex items-center"
                    >
                      <Building2 className="h-4 w-4 mr-2" />
                      Empresa *
                    </Label>
                    <div className="flex gap-2 mt-1">
                      <Select
                        key={companies.length}
                        value={formData.client_company_id?.toString() || ""}
                        onValueChange={(val) => {
                          handleChange("client_company_id", val);
                          handleChange("contact_id", "");
                        }}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Empresa" />
                        </SelectTrigger>
                        <SelectContent>
                          {companies.map((company) => (
                            <SelectItem key={company.id} value={company.id}>
                              {company.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setShowCompanyModal(true)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {errors.client_company_id && (
                      <p className="text-red-500 text-xs mt-1 font-medium">
                        {errors.client_company_id}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="contact_id" className="flex items-center">
                      <User className="h-4 w-4 mr-2" />
                      Contacto *
                    </Label>
                    <div className="flex gap-2 mt-1">
                      <Select
                        key={contacts.length}
                        value={formData.contact_id?.toString() || ""}
                        onValueChange={(val) => handleChange("contact_id", val)}
                        disabled={!formData.client_company_id}
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue
                            placeholder={
                              formData.client_company_id
                                ? "Seleccionar contacto"
                                : "Selecciona una empresa primero"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {contacts
                            .filter(
                              (contact) =>
                                contact.client_company_id ===
                                formData.client_company_id,
                            )
                            .map((contact) => (
                              <SelectItem key={contact.id} value={contact.id}>
                                {contact.name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setShowContactModal(true)}
                        disabled={!formData.client_company_id}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {errors.contact_id && (
                      <p className="text-red-500 text-xs mt-1 font-medium">
                        {errors.contact_id}
                      </p>
                    )}
                  </div>
                  {/**/}
                  <div>
                    <Label
                      htmlFor="opportunity_source_id"
                      className="flex items-center"
                    >
                      <Globe className="h-4 w-4 mr-2" />
                      Fuente
                    </Label>
                    <div className="flex gap-2 mt-1">
                      <Select
                        value={formData.opportunity_source_id}
                        onValueChange={(val) =>
                          handleChange("opportunity_source_id", val)
                        }
                      >
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Fuente" />
                        </SelectTrigger>
                        <SelectContent>
                          {sources.map((source) => (
                            <SelectItem key={source.id} value={source.id}>
                              {source.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setShowSourceModal(true)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {errors.opportunity_source_id && (
                      <p className="text-red-500 text-xs mt-1 font-medium">
                        {errors.opportunity_source_id}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label className="flex items-center">
                      <Users className="h-4 w-4 mr-2" />
                      Agregar en Copia
                    </Label>
                    <Select
                      onValueChange={(val) => {
                        if (!selectedCollaboratorIds.includes(val)) {
                          setSelectedCollaboratorIds((prev) => [...prev, val]);
                        }
                      }}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Añadir..." />
                      </SelectTrigger>
                      <SelectContent>
                        {users
                          .filter(
                            (u) =>
                              !selectedCollaboratorIds.includes(u.id) &&
                              u.id !== authUser?.id,
                          )
                          .map((u) => (
                            <SelectItem key={u.id} value={u.id}>
                              {getUserDisplayName(u)}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedCollaboratorIds.map((id) => {
                        const u = users.find((user) => user.id === id);
                        return (
                          <Badge
                            key={id}
                            variant="secondary"
                            className="gap-1 pr-1"
                          >
                            {u ? getUserDisplayName(u) : id}
                            <X
                              className="h-3 w-3 cursor-pointer"
                              onClick={() =>
                                setSelectedCollaboratorIds((prev) =>
                                  prev.filter((uid) => uid !== id),
                                )
                              }
                            />
                          </Badge>
                        );
                      })}
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="notes" className="flex items-center">
                      <FileText className="h-4 w-4 mr-2" />
                      Notas / Próximo Paso
                    </Label>
                    <Input
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => handleChange("notes", e.target.value)}
                      placeholder="Detalles adicionales..."
                      className="mt-1"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <FileUploader
                      attachments={attachments}
                      onAttachmentsChange={setAttachments}
                      maxFiles={5}
                      label="Adjuntos"
                    />
                  </div>
                </div>

                {isLostStage && (
                  <div className="bg-rose-50 p-4 rounded-lg border border-rose-200">
                    <Label className="text-rose-800 font-bold mb-2 block">
                      Motivo de pérdida
                    </Label>
                    <Textarea
                      value={formData.lost_reason}
                      onChange={(e) =>
                        handleChange("lost_reason", e.target.value)
                      }
                      placeholder="¿Por qué se perdió esta oportunidad?"
                      className="bg-white border-rose-200"
                    />
                    {errors.lost_reason && (
                      <p className="text-red-500 text-xs mt-1 font-bold">
                        {errors.lost_reason}
                      </p>
                    )}
                  </div>
                )}

                <div className="md:col-span-2 space-y-4 pt-4 border-t border-slate-100">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold uppercase tracking-widest text-slate-500">
                      Listado de Productos
                    </h3>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setIsEditingExisting(false);
                        setShowAddProductModal(true);
                      }}
                      className="bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100"
                    >
                      <Plus className="h-4 w-4 mr-1" /> Añadir Producto
                    </Button>
                  </div>

                  {opportunityItems.length > 0 && (
                    <div className="space-y-2">
                      {opportunityItems.map((item: any) => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg group hover:border-blue-300 transition-all"
                        >
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-slate-100 rounded-md">
                              <Package className="h-4 w-4 text-slate-600" />
                            </div>
                            <div>
                              <p className="text-sm font-semibold">
                                {item.product?.name || item.description}
                              </p>
                              <div className="mt-1 w-full">
                                {(() => {
                                  const catalogPrice = item.product
                                    ? parseFloat(
                                        item.original_price?.toString() ||
                                          item.product.base_price?.toString() ||
                                          "0",
                                      )
                                    : parseFloat(
                                        item.base_price?.toString() || "0",
                                      );
                                  const numericBasePrice = parseFloat(
                                    item.base_price?.toString() || "0",
                                  );
                                  const isModified =
                                    item.product &&
                                    (item.has_deviation ||
                                      Math.abs(
                                        numericBasePrice - catalogPrice,
                                      ) > 0.01);

                                  const baseSubtotal =
                                    numericBasePrice * item.quantity;
                                  const lineTotal = parseFloat(
                                    item.line_total?.toString() || "0",
                                  );
                                  const extrasTotal = lineTotal - baseSubtotal;
                                  const customRulesOnly =
                                    item.applied_rules?.filter(
                                      (r: any) => !r.is_required,
                                    ) || [];
                                  const customCount = customRulesOnly.length;
                                  let customExtrasTotal = 0;

                                  const rulesCount =
                                    item.applied_rules?.length || 0;
                                  if (rulesCount > 0) {
                                    item.applied_rules.forEach((rule: any) => {
                                      if (!rule.is_required) {
                                        const modifier =
                                          typeof rule.price_modifier ===
                                          "string"
                                            ? parseFloat(rule.price_modifier)
                                            : rule.price_modifier || 0;
                                        let ruleTotal = 0;
                                        if (rule.price_type === "percentage") {
                                          ruleTotal =
                                            baseSubtotal * (modifier / 100);
                                        } else if (
                                          rule.price_type === "per_unit"
                                        ) {
                                          ruleTotal = modifier * item.quantity;
                                        } else {
                                          ruleTotal = modifier;
                                        }
                                        customExtrasTotal += ruleTotal;
                                      }
                                    });
                                  }

                                  return (
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500">
                                      <span>
                                        <span className="font-bold text-slate-700">
                                          Cant:
                                        </span>{" "}
                                        {item.quantity}
                                      </span>
                                      <span className="flex items-center gap-1">
                                        <span className="font-bold text-slate-700">
                                          P. Base:
                                        </span>
                                        {isModified && (
                                          <span className="line-through text-slate-400">
                                            {currencySymbol}
                                            {formatCurrency(catalogPrice)}
                                          </span>
                                        )}
                                        <span
                                          className={`${isModified ? "text-orange-600 font-bold" : ""}`}
                                        >
                                          {currencySymbol}
                                          {formatCurrency(numericBasePrice)}
                                          {isModified && " (Modif.)"}
                                        </span>
                                      </span>
                                      {customCount > 0 && (
                                        <span className="text-blue-600 font-bold bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100 flex items-center">
                                          {customCount}{" "}
                                          {customExtrasTotal < 0
                                            ? customCount === 1
                                              ? "Descuento"
                                              : "Descuentos"
                                            : customCount === 1
                                              ? "Servicio"
                                              : "Servicios"}{" "}
                                          ({customExtrasTotal >= 0 ? "+" : "-"}
                                          {currencySymbol}
                                          {formatCurrency(
                                            Math.abs(customExtrasTotal),
                                          )}
                                          )
                                        </span>
                                      )}
                                      <span className="font-bold text-slate-800 ml-auto bg-slate-100 px-2 py-0.5 rounded">
                                        Total: {currencySymbol}
                                        {formatCurrency(lineTotal)}
                                      </span>
                                    </div>
                                  );
                                })()}
                              </div>

                              {/* Seccion de Notas y Servicios */}
                              {(item.notes ||
                                (item.applied_rules &&
                                  item.applied_rules.length > 0)) && (
                                <div className="mt-2 pl-3 border-l-2 border-slate-200 space-y-1">
                                  {item.notes && (
                                    <p className="text-[10px] text-slate-600">
                                      <span className="font-semibold text-slate-700">
                                        Notas:
                                      </span>{" "}
                                      {item.notes}
                                    </p>
                                  )}
                                  {(() => {
                                    const { required, custom } = separateRules(
                                      item.applied_rules || [],
                                    );
                                    return (
                                      <>
                                        {required.length > 0 && (
                                          <div className="mt-1">
                                            <span className="font-semibold text-[11px] text-rose-700 block mb-1">
                                              Ajustes Obligatorios:
                                            </span>
                                            <div className="flex flex-wrap gap-1">
                                              {required.map(
                                                (r: any, idx: number) => (
                                                  <Badge
                                                    key={`req-${idx}`}
                                                    variant="destructive"
                                                    className="text-[10px] py-0 px-1.5 h-4 bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100"
                                                  >
                                                    {r.rule_name || "Ajuste"}{" "}
                                                    {r.calculated_amount
                                                      ? `(${parseFloat(r.calculated_amount) >= 0 ? "+" : ""}${r.calculated_amount})`
                                                      : ""}
                                                  </Badge>
                                                ),
                                              )}
                                            </div>
                                          </div>
                                        )}
                                        {custom.length > 0 && (
                                          <div className="mt-1">
                                            <span className="font-semibold text-[11px] text-blue-700 block mb-1">
                                              Servicios / Extras:
                                            </span>
                                            <div className="flex flex-wrap gap-1">
                                              {custom.map(
                                                (r: any, idx: number) => (
                                                  <Badge
                                                    key={`cust-${idx}`}
                                                    variant="secondary"
                                                    className="text-[10px] py-0 px-1.5 h-4 bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                                                  >
                                                    {r.rule_name || "Servicio"}{" "}
                                                    {r.description
                                                      ? `- ${r.description}`
                                                      : ""}{" "}
                                                    {r.calculated_amount
                                                      ? `(${parseFloat(r.calculated_amount) >= 0 ? "+" : ""}${r.calculated_amount})`
                                                      : ""}
                                                  </Badge>
                                                ),
                                              )}
                                            </div>
                                          </div>
                                        )}
                                      </>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-slate-400 hover:text-blue-600"
                              onClick={() => {
                                setSelectedItemForRules(item);
                                setIsEditingExisting(true);
                                setShowAddProductModal(true);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-slate-400 hover:text-rose-600"
                              onClick={() => handleDeleteItem(item.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Summary & Discount Card - Same as Create but with Totals from State */}
                  {opportunityItems.length > 0 && (
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                      <div className="flex flex-col md:flex-row justify-between gap-6">
                        <div className="space-y-3">
                          <Label className="text-xs font-bold text-slate-500 uppercase">
                            Descuento Global
                          </Label>
                          <div className="flex items-center gap-2">
                            <Input
                              value={formatDisplayValue(discount.value)}
                              onChange={(e) =>
                                handleDiscountChange(e.target.value)
                              }
                              className="w-24 h-9 bg-white"
                              placeholder="0.00"
                            />
                            <div className="flex bg-white rounded-md p-0.5 border border-slate-200">
                              <button
                                type="button"
                                onClick={() =>
                                  setDiscount({
                                    ...discount,
                                    type: "percentage",
                                  })
                                }
                                className={`p-1.5 rounded-md ${discount.type === "percentage" ? "bg-slate-900 text-white shadow-sm" : "text-slate-400 hover:text-slate-600"}`}
                              >
                                <Percent className="h-3 w-3" />
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  setDiscount({ ...discount, type: "fixed" })
                                }
                                className={`p-1.5 rounded-md ${discount.type === "fixed" ? "bg-slate-900 text-white shadow-sm" : "text-slate-400 hover:text-slate-600"}`}
                              >
                                <Tag className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="w-full md:w-64 space-y-2">
                          {(() => {
                            const totals = getDetailedTotals();
                            return (
                              <>
                                <div className="space-y-1.5 pb-3 border-b border-slate-200 mb-2 max-h-40 overflow-y-auto custom-scrollbar pr-1">
                                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
                                    Resumen de Ítems
                                  </p>
                                  {opportunityItems.map(
                                    (item: any, i: number) => {
                                      const lineTotal = parseFloat(
                                        item.line_total?.toString() || "0",
                                      );
                                      const name =
                                        item.product?.name ||
                                        item.description ||
                                        "Producto";
                                      return (
                                        <div
                                          key={i}
                                          className="flex justify-between items-start text-[11px] text-slate-600 gap-2"
                                        >
                                          <span
                                            className="truncate flex-1"
                                            title={name}
                                          >
                                            {item.quantity}x {name}
                                          </span>
                                          <span className="font-medium">
                                            {currencySymbol}
                                            {formatCurrency(lineTotal)}
                                          </span>
                                        </div>
                                      );
                                    },
                                  )}
                                </div>
                                <div className="flex justify-between items-center text-xs font-semibold text-slate-700">
                                  <span>Subtotal</span>
                                  <span>
                                    {currencySymbol}
                                    {formatCurrency(totals.subtotal)}
                                  </span>
                                </div>
                                {totals.discountAmount > 0 && (
                                  <div className="flex justify-between items-center text-xs text-red-600">
                                    <span>Descuento Global</span>
                                    <span>
                                      -{currencySymbol}
                                      {formatCurrency(totals.discountAmount)}
                                    </span>
                                  </div>
                                )}
                                {totals.tax > 0 && (
                                  <div className="flex justify-between items-center text-xs text-slate-500">
                                    <span>IVA / Impuestos</span>
                                    <span>
                                      {currencySymbol}
                                      {formatCurrency(totals.tax)}
                                    </span>
                                  </div>
                                )}
                                <div className="flex justify-between items-center pt-2 border-t border-slate-200 mt-2">
                                  <span className="text-sm font-bold text-slate-900">
                                    Total Neto
                                  </span>
                                  <span className="text-xl font-bold text-blue-700">
                                    {formatCurrency(totals.finalTotal)}
                                  </span>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter className="p-6 border-t border-slate-100 flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSaving}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSaving}
                className="bg-slate-900 hover:bg-black text-white px-8"
              >
                {isSaving ? "Guardando..." : "Guardar Cambios"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <CurrencyModal
        isOpen={showCurrencyModal}
        onClose={() => setShowCurrencyModal(false)}
        slug={slug}
      />

      <AddProductModal
        isOpen={showAddProductModal}
        onClose={() => setShowAddProductModal(false)}
        allProducts={products}
        slug={slug}
        onRefreshProducts={() => {
          productService.getProducts({ slug, limit: 100 }).then((res) => {
            setProducts(res.data || []);
          });
        }}
        onAddProduct={handleAddOrUpdateProduct}
        initialData={
          isEditingExisting && selectedItemForRules
            ? {
                id: selectedItemForRules.id,
                product_id: selectedItemForRules.product?.id || "",
                quantity: selectedItemForRules.quantity || 1,
                notes: selectedItemForRules.notes || "",
                base_price: parseFloat(selectedItemForRules.base_price) || 0,
                applied_rules:
                  selectedItemForRules.applied_rules?.map((r: any) => {
                    const ruleObj = r.rule || {};
                    return {
                      rule_id: r.rule_id,
                      rule_name: r.rule_name || ruleObj.rule_name,
                      is_custom: r.is_custom,
                      price_modifier:
                        r.price_modifier ||
                        ruleObj.price_modifier ||
                        r.calculated_amount,
                      price_type: r.price_type || ruleObj.price_type,
                      description: r.description || "",
                    };
                  }) || [],
              }
            : null
        }
      />

      <CreateCompanyModal
        isOpen={showCompanyModal}
        onClose={() => setShowCompanyModal(false)}
        companySlug={slug}
        onSave={async (newCompanyData) => {
          try {
            const response = await companyAPI.createCompany(newCompanyData);
            if (response.data) {
              setCompanies((prev) => [...prev, response.data]);
              setFormData((prev) => ({
                ...prev,
                client_company_id: response.data.id,
              }));
            }
          } catch (error) {
            console.error(error);
          }
        }}
      />

      <CreateContactModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        defaultCompanyId={formData.client_company_id || undefined}
        defaultCompanyName={
          companies.find((c) => c.id === formData.client_company_id)?.name ||
          undefined
        }
        onSave={async (newContact) => {
          try {
            const response = await contactoAPI.createContacto(slug, newContact);
            const contactId = (response as any).id || (response as any).ID;
            setContacts((prev) => [...prev, response]);
            setFormData((prev) => ({ ...prev, contact_id: contactId }));
          } catch (error) {
            console.error(error);
          }
        }}
      />

      <OpportunitySourcesModal
        isOpen={showSourceModal}
        onClose={() => setShowSourceModal(false)}
        slug={slug}
        onSourceCreated={fetchData}
      />
    </>
  );
}
